import type * as SentryModule from '@sentry/react';
import type { Breadcrumb, ErrorEvent } from '@sentry/react';

/**
 * Error tracking for the console (Marzouki-org/Marzouki-Admin#47): Sentry, and only when
 * a DSN was compiled in.
 *
 * **Inert without `VITE_SENTRY_DSN`.** No DSN at build time, and `initErrorTracking`
 * returns before the SDK is even downloaded: `@sentry/react` is a dynamic import, so a
 * build without a DSN ships the SDK as a chunk nobody requests, and `pnpm dev`, the
 * tests and every environment that has not opted in send nothing anywhere.
 *
 * **The DSN is public, and that is fine.** Like every `VITE_*` value it is inlined into
 * the bundle every browser downloads (CONTRIBUTING §7). A DSN only lets a client *send*
 * events to the project; it is not a credential and reads nothing back. What it does
 * permit is noise, which the project's rate limits and allowed-domains filter are for.
 *
 * **No personal data leaves.** The console shows talents' names, phones, emails and
 * documents; none of that belongs in a third-party error log. The SDK is told to collect
 * no user, cookies, headers, bodies, query strings or stack locals, and every event and
 * breadcrumb then goes through `scrubEvent` / `scrubBreadcrumb` as well, because an
 * error *message* can carry a value the options never see.
 */

export interface ErrorTrackingEnv {
  readonly VITE_SENTRY_DSN?: string;
  readonly VITE_SENTRY_ENVIRONMENT?: string;
  readonly VITE_SENTRY_RELEASE?: string;
}

type CaptureFn = (error: unknown) => void;

let capture: CaptureFn | null = null;

/**
 * Starts Sentry when a DSN was built in; resolves to whether it did. Never throws: a
 * console that cannot load its error reporter still has to load.
 */
export async function initErrorTracking(
  env: ErrorTrackingEnv = import.meta.env,
  load: () => Promise<typeof SentryModule> = () => import('@sentry/react'),
): Promise<boolean> {
  const dsn = env.VITE_SENTRY_DSN?.trim();
  if (!dsn) return false;

  try {
    const Sentry = await load();
    Sentry.init({
      dsn,
      environment: env.VITE_SENTRY_ENVIRONMENT?.trim() || 'local',
      ...(env.VITE_SENTRY_RELEASE?.trim() ? { release: env.VITE_SENTRY_RELEASE.trim() } : {}),
      // Errors only. Tracing and replay record what an admin looks at, which is
      // people's records; neither is wanted.
      tracesSampleRate: 0,
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: false,
        httpBodies: [],
        urlQueryParams: false,
        stackFrameVariables: false,
      },
      beforeSend: (event) => scrubEvent(event),
      beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
    });
    capture = (error) => {
      Sentry.captureException(error);
    };
    return true;
  } catch {
    return false;
  }
}

/**
 * Reports an error React caught or could not catch. A no-op until `initErrorTracking`
 * has started Sentry, which is always the case without a DSN.
 */
export function reportError(error: unknown): void {
  capture?.(error);
}

/** For the specs: forget a started client. */
export function resetErrorTracking(): void {
  capture = null;
}

const EMAIL = /[\w.+-]+@[\w-]+(\.[\w-]+)+/g;
// Saudi mobiles in every shape the console handles (+9665…, 009665…, 05…), and any
// longer run of digits that could be a phone or a national ID.
const PHONE_OR_ID = /\+?\d[\d\s-]{7,}\d/g;
const BEARER = /bearer\s+[\w.~+/-]+=*/gi;

/** Takes the values that identify a person, or would let someone act as one, out of a string. */
export function scrubText(text: string): string {
  return text
    .replace(BEARER, 'Bearer [redacted]')
    .replace(EMAIL, '[email]')
    .replace(PHONE_OR_ID, '[number]');
}

/** A URL without its query string or fragment: search terms are often a name or a phone. */
export function scrubUrl(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

export function scrubEvent(event: ErrorEvent): ErrorEvent {
  delete event.user;
  delete event.extra;
  if (event.request) {
    event.request = event.request.url ? { url: scrubUrl(event.request.url) } : {};
  }
  if (event.message) event.message = scrubText(event.message);
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) exception.value = scrubText(exception.value);
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs
      .map(scrubBreadcrumb)
      .filter((crumb): crumb is Breadcrumb => crumb !== null);
  }
  return event;
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // What an admin typed or clicked on is, in this console, a person's data.
  if (breadcrumb.category?.startsWith('ui.')) return null;

  const scrubbed: Breadcrumb = { ...breadcrumb };
  if (scrubbed.message) scrubbed.message = scrubText(scrubbed.message);
  if (scrubbed.data) {
    const data: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(scrubbed.data)) {
      if (key === 'url' || key === 'from' || key === 'to') {
        data[key] = typeof value === 'string' ? scrubUrl(value) : value;
      } else if (key === 'arguments') {
        // console.* breadcrumbs: the logged values themselves.
        continue;
      } else if (typeof value === 'string') {
        data[key] = scrubText(value);
      } else {
        data[key] = value;
      }
    }
    scrubbed.data = data;
  }
  return scrubbed;
}
